import React from 'react';
import {
  HCard,
  HearthIcon,
  SerifHeading,
  SectionLabel,
  HSecondaryButton,
} from '../h';
import { displayState, getCountryName } from '../../utils/shippingCountries';

/* Shared body used by PreparingForShipping (v9 1.6/1.7) and ThankYouPreShipment.

   Inputs:
     - phase: 'received' | 'preparing'
         received  → "Addresses confirmed, we'll let you know when shipping starts"
         preparing → "Your Hearths are on the way"
     - numOfFamilies: 1 = single Hearth (1.6), >1 = multi (1.7)
     - shippingAddresses: optional array per-Hearth from /confirmShippingAddresses
     - shippingAddressAtOrder + nameFromServer: fallback when shippingAddresses
       isn't available (backend gap noted in FEAT-133-BACKEND-NOTES.md)
*/
export function ShippedSummary({
  email,
  numOfFamilies = 1,
  shippingAddresses,
  shippingAddressAtOrder,
  nameFromServer,
  phase = 'preparing',
}) {
  const isMulti = numOfFamilies > 1;

  /* Resolve the addresses list. Prefer the proper per-Hearth array; otherwise
     fall back to a single address built from order-time fields (single-Hearth
     case mostly). */
  const resolvedAddresses =
    Array.isArray(shippingAddresses) && shippingAddresses.length > 0
      ? shippingAddresses
      : shippingAddressAtOrder
        ? [
            {
              ...shippingAddressAtOrder,
              name: shippingAddressAtOrder.name || nameFromServer || '',
            },
          ]
        : [];

  // "Split" = more than one Hearth AND they're not all going to the same
  // address. A multi-Hearth order that all ships to one address is NOT a
  // split — it gets a single combined address block, not one block per Hearth.
  const isSplit =
    resolvedAddresses.length > 1 &&
    new Set(resolvedAddresses.map(addressKey)).size > 1;

  // "preparing" = admin has flipped preparingForShipping=true but nothing
  // has shipped yet. Addresses are locked; device is NOT on the way yet.
  // Matches OLD PreparingForShipping copy.
  const heading =
    phase === 'received'
      ? isMulti
        ? 'Addresses confirmed'
        : 'Address confirmed'
      : isMulti
        ? 'We’re getting your Hearths ready'
        : 'We’re getting your Hearth ready';

  const subtitle =
    phase === 'received'
      ? isSplit
        ? 'Shipping addresses successfully recorded — we’ll email you the tracking number for each Hearth when they ship.'
        : isMulti
          ? 'Shipping address successfully recorded — we’ll email you the tracking number when they ship.'
          : 'Shipping address successfully recorded — we’ll email you the tracking number when your Hearth ships.'
      : isSplit
        ? "They're being packed now, and your addresses are locked and can't be changed any more. We'll email you the tracking numbers when they ship."
        : "They're being packed now, and your address is locked and can't be changed any more. We'll email you the tracking number when they ship.";

  return (
    <div className="px-4 pt-4 pb-6">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-amber-100">
          <TruckIcon />
        </div>
      </div>

      <SerifHeading
        size={
          phase === 'preparing' && (isMulti || (shippingAddresses?.length || 0) > 1)
            ? 'md'
            : 'lg'
        }
        className="text-center mb-2"
      >
        {heading}
      </SerifHeading>
      <p className="text-[13px] text-slate-600 text-center leading-relaxed mb-4 mx-auto max-w-[320px]">
        {subtitle}
      </p>

      {resolvedAddresses.length > 0 && (
        <HCard className="p-4 mb-4">
          {!isSplit ? (
            <>
              <SectionLabel className="mb-2">
                {isMulti ? `Shipping all ${numOfFamilies} Hearths to` : 'Shipping to'}
              </SectionLabel>
              <div className="flex items-center gap-2 mb-2">
                <HearthIcon size="sm" />
              </div>
              <div className="text-[13px] text-slate-700 leading-relaxed">
                <strong>{resolvedAddresses[0].name}</strong>
                <br />
                {formatAddressLine(resolvedAddresses[0])}
                {resolvedAddresses[0].country && (
                  <>
                    <br />
                    {getCountryName(resolvedAddresses[0].country)}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {resolvedAddresses.map((address, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 ${
                    idx > 0 ? 'pt-3 mt-3 border-t border-slate-100' : ''
                  }`}
                >
                  <HearthIcon size="sm" className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-slate-900 mb-1">
                      Hearth {idx + 1}
                    </div>
                    <div className="text-[11px] text-slate-600 leading-relaxed">
                      {address.name}
                      <br />
                      {formatAddressLine(address)}
                      {address.country && (
                        <>
                          <br />
                          {getCountryName(address.country)}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </HCard>
      )}

      <HCard variant="next" className="p-4 mb-4">
        <div className="text-[13px] text-slate-700 leading-relaxed">
          {isMulti ? (
            <>
              <strong>Once your Hearths arrive</strong>, you can come back here and
              we&rsquo;ll walk you through how to set up yours, and how to generate a
              link for other family members to set up theirs.
            </>
          ) : (
            <>
              <strong>Once the box arrives</strong>, sign back in here — we&rsquo;ll walk
              you through setup.
            </>
          )}
        </div>
      </HCard>

      <HSecondaryButton
        onClick={() => {
          window.location.href = 'mailto:hq@higlo.co?subject=Question%20about%20my%20Higlo%20order';
        }}
        className="w-full"
      >
        Contact us about your order
      </HSecondaryButton>

    </div>
  );
}

function TruckIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d97706"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function addressKey(a) {
  // Normalised key used to decide whether two Hearth addresses are "the same".
  // Compares the meaningful fields only, case/whitespace-insensitive.
  if (!a) return '';
  return [a.name, a.line1, a.line2, a.city, a.state, a.postalCode, a.country]
    .map((v) => (v || '').trim().toLowerCase())
    .join('|');
}

// "line1, line2, city, STATE postcode" — the state is the upper-case code for
// US/CA and the free-text county/region elsewhere (omitted when empty).
function formatAddressLine(a) {
  if (!a) return null;
  const statePostcode = [displayState(a.state, a.country), a.postalCode].filter(Boolean).join(' ');
  return [a.line1, a.line2, a.city, statePostcode].filter(Boolean).join(', ');
}
